import { useState } from "react";
import { Ed25519Keypair } from "@iota/iota-sdk/keypairs/ed25519";
import { buildSeedRecoveryFile, decryptRecoveryFile, downloadRecoveryFile, generateSeedHex, parseRecoveryFile } from "./seed-recovery.js";
import { useI18n } from "./i18n.jsx";

async function authenticateDid(did, seed, t) {
  if (!String(did).trim()) throw new Error(t("didRequired"));
  if (!String(seed ?? "").trim()) throw new Error(t("seedRequired"));
  const keypair = Ed25519Keypair.deriveKeypairFromSeed(String(seed).trim().replace(/^0x/i, ""));
  const challengeResponse = await fetch("/api/auth/challenge", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ did: String(did).trim() }) });
  const challenge = await challengeResponse.json();
  if (!challengeResponse.ok) throw new Error(challenge.error || t("challengeError"));
  const signed = await keypair.signPersonalMessage(new TextEncoder().encode(challenge.message));
  const verifyResponse = await fetch("/api/auth/verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ challengeId: challenge.challengeId, did: String(did).trim(), signature: signed.signature }) });
  const session = await verifyResponse.json();
  if (!verifyResponse.ok) throw new Error(session.error || t("authError"));
  return session;
}

export function AccessDialog({ mode, network = "testnet", onClose, onAuthenticated, onMode }) {
  return mode === "register"
    ? <Registration onClose={onClose} onAuthenticated={onAuthenticated} onMode={onMode} />
    : <Login network={network} onClose={onClose} onAuthenticated={onAuthenticated} onMode={onMode} />;
}

function Login({ network, onClose, onAuthenticated, onMode }) {
  const { t } = useI18n();
  const [did, setDid] = useState("");
  const [seed, setSeed] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event) {
    event.preventDefault(); setBusy(true); setError("");
    try {
      if (recovery && !password) throw new Error(t("recoveryPasswordRequired"));
      const loginSeed = recovery ? await decryptRecoveryFile(recovery, password) : seed;
      onAuthenticated(await authenticateDid(did, loginSeed, t));
    } catch (cause) { setError(cause instanceof Error ? cause.message : String(cause)); }
    finally { setSeed(""); setBusy(false); }
  }
  async function chooseFile(event) {
    try { const file = event.target.files?.[0]; if (!file) return; const parsed = parseRecoveryFile(await file.text()); setRecovery(parsed); if (parsed.did) setDid(parsed.did); setError(""); }
    catch (cause) { setError(cause.message); setRecovery(null); }
  }

  return <Modal onClose={onClose} busy={busy} eyebrow={t("loginEyebrow")} title={t("loginTitle")}>
    <p>{t("loginIntro")}</p>
    <form onSubmit={submit} className="auth-form">
      <label>{t("iotaDid")}<input value={did} onChange={(event) => setDid(event.target.value)} placeholder={network === "mainnet" ? "did:iota:0x…" : `did:iota:${network}:0x…`} required /></label>
      <label>{t("seed")}<input value={seed} onChange={(event) => setSeed(event.target.value)} type="password" placeholder={recovery ? t("seedFromRecovery") : t("identitySeed")} disabled={Boolean(recovery)} /></label>
      <label className="file-input">{t("loadRecovery")}<input type="file" accept="application/json,.json" onChange={chooseFile} /></label>
      {recovery && <label>{t("recoveryPassword")} <small>{t("hint")}: {recovery.hint}</small><input value={password} onChange={(event) => setPassword(event.target.value)} type="password" required /></label>}
      {error && <div className="form-error">{error}</div>}
      <div className="modal-actions"><button type="button" className="ghost" onClick={() => onMode("register")}>{t("createIdentity")}</button><button type="submit" disabled={busy}>{busy ? t("verifyingIota") : t("signIn")}</button></div>
    </form>
    <small className="security-note">{t("authSecurity")}</small>
  </Modal>;
}

function Registration({ onClose, onAuthenticated, onMode }) {
  const { t } = useI18n();
  const [seed, setSeed] = useState(() => generateSeedHex());
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [hint, setHint] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const address = (() => { try { return Ed25519Keypair.deriveKeypairFromSeed(seed).toIotaAddress(); } catch { return ""; } })();

  async function submit(event) {
    event.preventDefault(); setError("");
    if (password.length < 10) return setError(t("passwordLength"));
    if (password !== confirm) return setError(t("passwordMismatch"));
    if (!hint.trim() || !accepted) return setError(t("completeRecovery"));
    setBusy(true);
    try {
      const keypair = Ed25519Keypair.deriveKeypairFromSeed(seed);
      setStatus(t("creatingDid"));
      const { registerIdentity } = await import("./identity-registration.js");
      const registration = await registerIdentity(keypair, "client");
      setStatus(t("encryptingRecovery"));
      downloadRecoveryFile(await buildSeedRecoveryFile({ seed, password, hint, did: registration.did }), registration.did);
      setStatus(t("accessingIdentity"));
      onAuthenticated(await authenticateDid(registration.did, seed, t));
    } catch (cause) { setError(cause instanceof Error ? cause.message : String(cause)); setStatus(""); }
    finally { setBusy(false); }
  }

  return <Modal onClose={onClose} busy={busy} eyebrow={t("registerEyebrow")} title={t("registerTitle")}>
    <p>{t("registerIntro")}</p>
    <form onSubmit={submit} className="auth-form">
      <div className="address-preview"><span>{t("signerAddress")}</span><code>{address || t("invalidSeed")}</code></div>
      <label>{t("newSeed")}<div className="inline-input"><input value={seed} onChange={(event) => setSeed(event.target.value.trim())} type="password" required /><button type="button" className="ghost" onClick={() => setSeed(generateSeedHex())}>{t("regenerate")}</button></div></label>
      <div className="form-grid"><label>{t("recoveryPasswordLabel")}<input value={password} onChange={(event) => setPassword(event.target.value)} type="password" /></label><label>{t("confirmPassword")}<input value={confirm} onChange={(event) => setConfirm(event.target.value)} type="password" /></label></div>
      <label>{t("passwordHint")}<input value={hint} onChange={(event) => setHint(event.target.value)} placeholder={t("passwordHintPlaceholder")} /></label>
      <label className="check-row"><input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} /><span>{t("recoveryConfirm")}</span></label>
      {status && <div className="working">◌ {status}</div>}{error && <div className="form-error">{error}</div>}
      <div className="modal-actions"><button type="button" className="ghost" onClick={() => onMode("login")}>{t("alreadyHaveDid")}</button><button type="submit" disabled={busy}>{busy ? t("pleaseWait") : t("createDidBackup")}</button></div>
    </form>
  </Modal>;
}

function Modal({ onClose, busy, eyebrow, title, children }) {
  return <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && !busy && onClose()}><section className="modal auth-modal"><button className="modal-close" onClick={onClose} disabled={busy}>×</button><span className="eyebrow">{eyebrow}</span><h2>{title}</h2>{children}</section></div>;
}
